import { useI18n } from '@/lib/i18n';
import { useGlobalSelection, type SelectionEntry } from '@/hooks/useGlobalSelection';
import { useAppData } from '@/hooks/useAppData';
import { useWidgetPageState } from '@/hooks/useWidgetPageState';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { Copy, ArrowRightLeft, Trash2, FolderInput, LayoutGrid, Pencil, Image, FileImage, Files } from 'lucide-react';
import { copySelectedToArea, moveSelectedToArea, moveSelectedWidgetsToPage } from '@/lib/deleteActions';
import { toast } from 'sonner';
import type { ReactNode } from 'react';


interface ItemContextMenuProps {
  children: ReactNode;
  /** Current area of the items */
  area: 'widget' | 'history' | 'folder' | 'folder-node';
  /** Called when delete is requested */
  onDelete: () => void;
  onRename?: (itemId: string) => void;
  onNewFolder?: (parentId: string | null) => void;
  itemId?: string;
  dataId?: string;
  onRightClickSelect?: (itemId: string, dataId: string) => void;
  /** Whether the item has content (for folders) */
  hasItems?: boolean;
}

const ItemContextMenu = ({
  children,
  area,
  onDelete,
  itemId,
  dataId,
  onRename,
  onNewFolder,
  onRightClickSelect,
  hasItems = true,
}: ItemContextMenuProps) => {
  const { t } = useI18n();
  const { entries, totalCount, clearArea } = useGlobalSelection();
  const { folders, widgetPages = [], getDataById } = useAppData();
  const { currentPageId } = useWidgetPageState();


  // Determine valid copy/move targets based on area rules:
  // widget → folder ✅ | history ❌
  // history → folder ✅ | widget ✅
  // folder → widget ✅ | history ❌
  // folder-node → widget ✅ | history ❌ | folder ✅
  const canCopyToWidget = (area === 'history' || area === 'folder' || area === 'folder-node') && (area !== 'folder-node' || hasItems);
  const canCopyToFolder = (area === 'widget' || area === 'history' || area === 'folder' || area === 'folder-node') && (area !== 'folder-node' || hasItems);
  const widgetEntries = entries.filter((entry) => entry.area === 'widget');
  const targetPages = widgetPages.filter((page) => page.id !== currentPageId);
  const canMoveToPage = area === 'widget' && widgetEntries.length > 0 && targetPages.length > 0;

  const handleCopyToWidget = () => {
    if (entries.length === 0) return;
    void copySelectedToArea(entries, 'widget', undefined, currentPageId);
  };

  const handleCopyToFolder = (folderId: string) => {
    if (entries.length === 0) return;
    void copySelectedToArea(entries, 'folder', folderId);
  };

  const handleMoveToWidget = () => {
    if (entries.length === 0) return;
    void moveSelectedToArea(entries, 'widget', undefined, currentPageId);
  };

  const handleMoveToFolder = (folderId: string) => {
    if (entries.length === 0) return;
    void moveSelectedToArea(entries, 'folder', folderId);
  };

  const handleMoveToPage = (pageId: string) => {
    if (widgetEntries.length === 0) return;
    void moveSelectedWidgetsToPage(widgetEntries, pageId).then((movedCount) => {
      if (movedCount > 0) clearArea('widget');
    });
  };

  const rootFolders = folders.filter(f => f.parentId === null);

  const handleContextMenu = () => {
    if (itemId && onRightClickSelect) {
      onRightClickSelect(itemId, dataId || '');
    }
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild onContextMenu={handleContextMenu}>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent className="glass-panel-static border-border/50 min-w-[180px]">
        {totalCount > 0 && (
          <>
            <div className="px-2 py-1.5 text-[10px] text-muted-foreground/60">
              {totalCount}개 항목 선택됨
            </div>
            <ContextMenuSeparator />

            {/* Copy submenu */}
            {(canCopyToWidget || (canCopyToFolder && rootFolders.length > 0)) && (
              <ContextMenuSub>
                <ContextMenuSubTrigger className="gap-2">
                  <Copy className="w-3.5 h-3.5" />
                  {t('context.copy')}
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className="glass-panel-static border-border/50 min-w-[160px]">
                  {canCopyToWidget && (
                    <ContextMenuItem onClick={handleCopyToWidget} className="gap-2">
                      <LayoutGrid className="w-3.5 h-3.5" />
                      {t('context.to_widget')}
                    </ContextMenuItem>
                  )}
                  {canCopyToFolder && rootFolders.length > 0 && (
                    <ContextMenuSub>
                      <ContextMenuSubTrigger className="gap-2">
                        <FolderInput className="w-3.5 h-3.5" />
                        폴더로
                      </ContextMenuSubTrigger>
                      <ContextMenuSubContent className="glass-panel-static border-border/50 min-w-[140px]">
                        {rootFolders.map(f => (
                          <ContextMenuItem key={f.id} onClick={() => handleCopyToFolder(f.id)}>
                            {f.name}
                          </ContextMenuItem>
                        ))}
                      </ContextMenuSubContent>
                    </ContextMenuSub>
                  )}
                </ContextMenuSubContent>
              </ContextMenuSub>
            )}

            {/* Move submenu */}
            {(canCopyToWidget || canMoveToPage || (canCopyToFolder && rootFolders.length > 0)) && (
              <ContextMenuSub>
                <ContextMenuSubTrigger className="gap-2">
                  <ArrowRightLeft className="w-3.5 h-3.5" />
                  {t('context.move')}
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className="glass-panel-static border-border/50 min-w-[160px]">
                  {canCopyToWidget && (
                    <ContextMenuItem onClick={handleMoveToWidget} className="gap-2">
                      <LayoutGrid className="w-3.5 h-3.5" />
                      {t('context.to_widget')}
                    </ContextMenuItem>
                  )}
                  {canMoveToPage && (
                    <ContextMenuSub>
                      <ContextMenuSubTrigger className="gap-2">
                        <Files className="w-3.5 h-3.5" />
                        {t('context.to_page')}
                      </ContextMenuSubTrigger>
                      <ContextMenuSubContent className="glass-panel-static border-border/50 min-w-[140px]">
                        {targetPages.map(page => (
                          <ContextMenuItem key={page.id} onClick={() => handleMoveToPage(page.id)}>
                            {page.name}
                          </ContextMenuItem>
                        ))}
                      </ContextMenuSubContent>
                    </ContextMenuSub>
                  )}
                  {canCopyToFolder && rootFolders.length > 0 && (
                    <ContextMenuSub>
                      <ContextMenuSubTrigger className="gap-2">
                        <FolderInput className="w-3.5 h-3.5" />
                        폴더로
                      </ContextMenuSubTrigger>
                      <ContextMenuSubContent className="glass-panel-static border-border/50 min-w-[140px]">
                        {rootFolders.map(f => (
                          <ContextMenuItem key={f.id} onClick={() => handleMoveToFolder(f.id)}>
                            {f.name}
                          </ContextMenuItem>
                        ))}
                      </ContextMenuSubContent>
                    </ContextMenuSub>
                  )}
                </ContextMenuSubContent>
              </ContextMenuSub>
            )}

            {/* Image specific actions */}
            {totalCount === 1 && dataId && getDataById(dataId)?.type === 'image' && (
              <>
                <ContextMenuSeparator />
                <ContextMenuItem
                  onClick={() => {
                    const data = getDataById(dataId);
                    if (!data) return;
                    const link = document.createElement('a');
                    link.href = data.content;
                    link.download = `${data.label || 'image'}.png`;
                    link.click();
                    toast.success(t('context.save_png_success'));
                  }}
                  className="gap-2"
                >
                  <FileImage className="w-3.5 h-3.5" />
                  {t('context.save_png')}
                </ContextMenuItem>
                <ContextMenuItem
                  onClick={async () => {
                    const data = getDataById(dataId);
                    if (!data) return;

                    try {
                      // Blob 방식으로 클립보드 복사
                      const response = await fetch(data.content);
                      const blob = await response.blob();
                      await navigator.clipboard.write([
                        new ClipboardItem({
                          [blob.type]: blob
                        })
                      ]);
                      toast.success(t('context.copy_image_success'));
                    } catch (err) {
                      console.error('Clipboard write failed:', err);
                      toast.error('지원하지 않는 브라우저이거나 복사에 실패했습니다.');
                    }
                  }}
                  className="gap-2"
                >
                  <Image className="w-3.5 h-3.5" />
                  {t('context.copy_image')}
                </ContextMenuItem>
                <ContextMenuItem
                  onClick={() => {
                    const data = getDataById(dataId);
                    if (!data) return;
                    navigator.clipboard.writeText(data.content);
                    toast.success(t('context.copy_base64_success'));
                  }}
                  className="gap-2"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {t('context.copy_base64')}
                </ContextMenuItem>
              </>
            )}

            <ContextMenuSeparator />


            {/* Delete */}
            <ContextMenuItem
              onClick={onDelete}
              className="gap-2 text-destructive focus:text-destructive"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {t('context.delete')}
            </ContextMenuItem>
          </>
        )}

        {area === 'folder-node' && itemId && onNewFolder && (
          <>
            {totalCount > 0 && <ContextMenuSeparator />}
            <ContextMenuItem onClick={() => onNewFolder(itemId)} className="gap-2">
              <FolderInput className="w-3.5 h-3.5" />
              새 폴더 생성
            </ContextMenuItem>
          </>
        )}

        {onRename && itemId && (
          <>
            {(totalCount > 0 || (area === 'folder-node' && onNewFolder)) && <ContextMenuSeparator />}
            <ContextMenuItem
              onClick={() => onRename(itemId)}
              className="gap-2"
            >
              <Pencil className="w-3.5 h-3.5" />
              {t('folder.rename')}
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>

  );
};

export default ItemContextMenu;
